<template>
  <div>
    <container-wrapper v-slot="slotProps" :element-key="elementKey" :element-data="elementData"
      display-name="Discussions and forums" icon-name="bi bi-chat-dots" :condensed="true" :can-subresults-open="true"
      :is-any-subresult-open="anyDiscussionOpen" @focus-view-click="$emit('focusViewClick')">

      <div v-for="(discussion, index) in elementData.discussionsandforumsanswers" 
        :id="'highlight_key_' + elementKey + '_' + index"
        :key="slotProps.accordionKey + index" 
        class="accordion-item discussion-border" 
        :data-urlid="discussion.urlid">
        <highlight-result :element-data="discussion" :type-id="'discussion_' + index" :class="getMarkupClasses(discussion)" class="accordion-header">
          <div
            class="discussion-container result-container d-flex align-items-center w-100 justify-content-between position-relative"
            :class="{ collapsed: !this.isOpen(discussion, index) }" 
            :title="discussion.title" 
            @click.stop="toggleOpen(discussion, index)">
            <div class="markup-container">
              <span class="discussion-title" :class="{ ellipsis: !this.isOpen(discussion, index) }">{{ discussion.title }}</span>
            </div>
            <span class="icons chevron-down-icon rotate text-primary" :class="{ up: isOpen(discussion, index) }" />
          </div>
        </highlight-result>
        <div :id="'key_' + elementKey + '_' + index" 
          class="accordion-collapse collapse"
          :class="[getMarkupClasses(discussion), { show: this.isOpen(discussion, index) }]">
          <div class="accordion-body">
            <div class="markup-container">
              <div class="d-flex flex-column discussion-wrapper pointer">
                <div class="answers-container">
                  <div v-for="(answer, answerIndex) in splitAnswers(discussion.desc)" 
                    :key="'answer_' + index + '_' + answerIndex"
                    class="answer-item">
                    <highlight-result :element-data="discussion" :type-id="'discussion_desc_' + index + '_' + answerIndex">
                      <span v-html="answer"></span>
                    </highlight-result>
                  </div>
                </div>
                <highlight-result :element-data="discussion" :type-id="'discussion_url_' + index">
                  <markup-link :resultData="cleanDiscussionData(discussion)"
                    :resultType="discussion.resulttype" />
                </highlight-result>
              </div>
            </div>
          </div>
        </div>
      </div>
    </container-wrapper>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import ContainerWrapper from "@/components/card/ContainerWrapper";
import MarkupLink from "@/components/card/MarkupLink.vue";
import HighlightResult from "@/components/card/HighlightResult.vue";

export default {
  name: "DiscussionsAndForumsBox",
  emits: ["focusViewClick"],
  props: {
    elementData: Object,
    elementKey: String,
  },
  components: {
    ContainerWrapper,
    MarkupLink,
    HighlightResult,
  },
  computed: {
    ...mapGetters("timeline", {
      isResultOpen: "isResultOpen",
    }),
    ...mapGetters("highlight", { highlightMode: "highlightMode" }),
    anyDiscussionOpen() {
      if (!this.elementData.discussionsandforumsanswers) return false;
      return this.elementData.discussionsandforumsanswers.some((discussion, index) => this.isOpen(discussion, index));
    },
    computedDesc() {
      return (desc) => {
        return desc || '';
      }
    }
  },
  methods: {
    ...mapActions("timeline", ["toggleResultOpen", "initClosedResults"]),
    titleKey(discussion, index) {
      return this.elementData.resulttype + '_' + index;
    },
    isOpen(discussion, index) {
      return this.isResultOpen(this.titleKey(discussion, index));
    },
    toggleOpen(discussion, index) {
      this.toggleResultOpen(this.titleKey(discussion, index));
    },
    splitAnswers(desc) {
      if (!desc) return [];
      // Split by ~~~ if present, otherwise return single answer
      const answers = desc.split('~~~').map(answer => answer.trim()).filter(answer => answer);
      return answers;
    },
    cleanDiscussionData(discussion) {
      // Create a copy of the discussion object and clean the desc field
      return {
        ...discussion,
        desc: discussion.desc ? discussion.desc.replace(/~~~/g, ' ').trim() : ''
      };
    },
  },
  mounted() {
    if (this.elementData.discussionsandforumsanswers) {
      const ids = this.elementData.discussionsandforumsanswers.map((d, index) => this.titleKey(d, index));
      this.initClosedResults(ids);
    }
  },
};
</script>

<style lang="scss" scoped>
@import "@/scss/_icons.scss";
@import "@/scss/_mixins.scss";

@mixin zoomViewSizes($type) {
  .#{$type}-view {
    .rotate {
      font-size: var(--timeline-accordion-toggle-font-size-#{$type});
      margin-right: calc(var(--timeline-accordion-toggle-right-offset-#{$type}) - 1px);
      align-self: baseline;
    }

    .discussion-container .ellipsis {
      max-width: var(--result-text-width-container-title-#{$type});
    }
  }
}

@include zoomViewSizes("regular");
@include zoomViewSizes("medium");

.card {
  .discussion-container {
    cursor: default;
    overflow: hidden;

    .rotate {
      line-height: 0px;
      display: inline-block;
      @include hover-clickable-effect;

      &.up {
        transform: rotate(-180deg);
        transition: transform 0.2s ease-in-out;
        @include hover-clickable-effect(rotate(-180deg));
      }
    }

    .markup-container {
      .discussion-title {
        color: var(--fb-blue-70);
      }
    }
  }

  .accordion-collapse {
    overflow: hidden;
    text-overflow: ellipsis;

    .accordion-body {
      padding: 0 0.05rem;

      .discussion-wrapper {
        max-width: 245px;
        overflow: auto;

        &>*:first-child {
          padding: 0.25rem 0 0.125rem 0;
        }
        
        &>*:last-child {
          padding: 0.125rem 0 0.25rem 0;
        }
      }

      .markup-container {
        opacity: 0.9;
        color: var(--fb-blue-70);
      }
    }
  }

  .discussion-border {
    border-bottom: 1px solid var(--fb-gray-20);

    &:last-child {
      border-bottom: none;
    }
  }
}
</style>